import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  Sparkles,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Zap,
  Target,
  Save,
  Globe,
  Flame,
  Coins,
  Users,
} from 'lucide-react';
import { storeService, type GlobalEvent, type FixedObjective } from '@/features/store/services/store.service';
import {
  gamificationService,
  type TeamXpOverrideRow,
} from '@/features/gamification/services/gamification.service';
import { Permission } from '@/common/enums/permissions.enum';
import { PermissionGate } from '@/components/PermissionGate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/_authenticated/admin/gamification-events')({
  component: GamificationEventsPage,
});

function GamificationEventsPage() {
  return (
    <PermissionGate permission={Permission.STORE_MANAGE}>
      <GamificationEventsContent />
    </PermissionGate>
  );
}

function GamificationEventsContent() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-500/10 border border-purple-500/30 rounded-md">
          <Sparkles className="h-6 w-6 text-purple-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Eventos & Objetivos</h1>
          <p className="text-muted-foreground text-sm">Crie eventos globais com multiplicadores de XP e gerencie objetivos fixos</p>
        </div>
      </div>

      <Tabs defaultValue="events">
        <TabsList>
          <TabsTrigger value="events">
            <Globe className="h-4 w-4 mr-1.5" />
            Eventos Globais
          </TabsTrigger>
          <TabsTrigger value="objectives">
            <Flame className="h-4 w-4 mr-1.5" />
            Objetivos Fixos
          </TabsTrigger>
          <TabsTrigger value="xp-rules">
            <Coins className="h-4 w-4 mr-1.5" />
            Regras de Pontos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-6">
          <EventsTab />
        </TabsContent>

        <TabsContent value="objectives" className="mt-6">
          <FixedObjectivesTab />
        </TabsContent>

        <TabsContent value="xp-rules" className="mt-6">
          <XpRulesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===========================
// EVENTS TAB
// ===========================

export function EventsTab() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<GlobalEvent | null>(null);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['admin-events'],
    queryFn: storeService.adminGetEvents,
  });

  const deleteMutation = useMutation({
    mutationFn: storeService.adminDeleteEvent,
    onSuccess: () => {
      toast.success('Evento excluído');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      queryClient.invalidateQueries({ queryKey: ['active-events'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      storeService.adminUpdateEvent(id, { active } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      queryClient.invalidateQueries({ queryKey: ['active-events'] });
    },
  });

  const now = new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground text-sm">{events.length} evento{events.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => { setEditingEvent(null); setShowForm(true); }} size="sm">
          <Plus className="h-4 w-4 mr-1.5" />
          Novo Evento
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="col-span-2 text-muted-foreground py-12 text-center">Carregando...</div>
        ) : events.length === 0 ? (
          <div className="col-span-2 text-muted-foreground py-12 text-center">
            <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum evento criado ainda</p>
          </div>
        ) : (
          events.map((event) => {
            const isExpired = event.endsAt && new Date(event.endsAt) < now;
            const isScheduled = event.startsAt && new Date(event.startsAt) > now;
            return (
              <Card key={event.id} className={`relative ${!event.active || isExpired ? 'opacity-70' : ''}`}>
                {event.active && !isExpired && !isScheduled && (
                  <div className="absolute top-3 right-3">
                    <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      ATIVO
                    </span>
                  </div>
                )}
                <CardContent className="pt-4 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap pr-16">
                      <h4 className="font-semibold">
                        {event.emoji && <span className="mr-1">{event.emoji}</span>}
                        {event.title}
                      </h4>
                      {Number(event.xpMultiplier) > 1 && (
                        <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 border-purple-300 dark:border-purple-700">
                          <Zap className="h-3 w-3 mr-1" />
                          ×{event.xpMultiplier} XP
                        </Badge>
                      )}
                      {isExpired && <Badge variant="secondary">Expirado</Badge>}
                      {isScheduled && <Badge variant="outline">Agendado</Badge>}
                    </div>
                    {event.description && (
                      <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                    )}
                  </div>

                  {(event.startsAt || event.endsAt) && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      {event.startsAt && (
                        <span>Início: {format(new Date(event.startsAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                      )}
                      {event.endsAt && (
                        <span>Fim: {format(new Date(event.endsAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={event.active}
                        onCheckedChange={(checked) => toggleActiveMutation.mutate({ id: event.id, active: checked })}
                        disabled={toggleActiveMutation.isPending}
                      />
                      <Label className="text-xs">{event.active ? 'Ativo' : 'Inativo'}</Label>
                    </div>
                    <div className="flex gap-1.5 ml-auto">
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => { setEditingEvent(event); setShowForm(true); }}>
                        <Pencil className="h-3 w-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs px-2 text-red-600 hover:text-red-700"
                        aria-label={`Excluir evento ${event.title}`}
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Excluir evento "${event.title}"?`,
                              description: 'Esta ação não pode ser desfeita.',
                              confirmLabel: 'Excluir evento',
                              destructive: true,
                            })
                          ) {
                            deleteMutation.mutate(event.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {showForm && (
        <EventFormDialog
          key={editingEvent?.id ?? 'novo'}
          open
          onClose={() => { setShowForm(false); setEditingEvent(null); }}
          event={editingEvent}
          queryClient={queryClient}
        />
      )}
    </div>
  );
}

function EventFormDialog({
  open,
  onClose,
  event,
  queryClient,
}: {
  open: boolean;
  onClose: () => void;
  event: GlobalEvent | null;
  queryClient: any;
}) {
  const [title, setTitle] = useState(event?.title ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [emoji, setEmoji] = useState(event?.emoji ?? '');
  const [xpMultiplier, setXpMultiplier] = useState(event ? String(event.xpMultiplier) : '1.5');
  const [active, setActive] = useState(event?.active ?? false);
  const [startsAt, setStartsAt] = useState(
    event?.startsAt ? format(new Date(event.startsAt), "yyyy-MM-dd'T'HH:mm") : '',
  );
  const [endsAt, setEndsAt] = useState(
    event?.endsAt ? format(new Date(event.endsAt), "yyyy-MM-dd'T'HH:mm") : '',
  );

  const createMutation = useMutation({
    mutationFn: storeService.adminCreateEvent,
    onSuccess: () => {
      toast.success('Evento criado!');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      queryClient.invalidateQueries({ queryKey: ['active-events'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<GlobalEvent> }) =>
      storeService.adminUpdateEvent(id, dto),
    onSuccess: () => {
      toast.success('Evento atualizado!');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      queryClient.invalidateQueries({ queryKey: ['active-events'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dto: any = {
      title,
      description: description || undefined,
      emoji: emoji || undefined,
      xpMultiplier: parseFloat(xpMultiplier),
      active,
      startsAt: startsAt || undefined,
      endsAt: endsAt || undefined,
    };

    if (event) {
      updateMutation.mutate({ id: event.id, dto });
    } else {
      createMutation.mutate(dto);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent size="md" busy={createMutation.isPending || updateMutation.isPending}>
        <DialogHeader>
          <DialogTitle>{event ? 'Editar Evento' : 'Novo Evento Global'}</DialogTitle>
          <DialogDescription>
            Eventos ativos mostram um banner no topo para todos os usuários. Podem multiplicar o XP ganho.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label>Emoji</Label>
              <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="🎉" maxLength={10} className="text-center text-xl" />
            </div>
            <div className="col-span-3 space-y-2">
              <Label>Título *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Nome do evento" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalhes do evento..." rows={2} />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-yellow-500" />
              Multiplicador de XP
            </Label>
            <Input
              type="number"
              step="0.1"
              min="1"
              max="5"
              value={xpMultiplier}
              onChange={(e) => setXpMultiplier(e.target.value)}
              placeholder="1.5"
            />
            <p className="text-xs text-muted-foreground">
              Ex: 1.5 = todos ganham 50% mais XP durante o evento. 1.0 = sem multiplicador.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Início</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fim</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label>Ativar evento imediatamente</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {event ? 'Salvar alterações' : 'Criar evento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===========================
// FIXED OBJECTIVES TAB
// ===========================

function FixedObjectivesTab() {
  const queryClient = useQueryClient();

  const { data: objectives = [], isLoading } = useQuery({
    queryKey: ['fixed-objectives'],
    queryFn: storeService.getFixedObjectives,
  });

  return (
    <div className="space-y-4">
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/10">
        <CardContent className="pt-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Objetivos fixos são definidos pelo sistema e não podem ser criados nem excluídos, apenas editados. 
            Eles implementam comportamentos especiais de gamificação como o streak "OnFire".
          </p>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-muted-foreground py-12 text-center">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {objectives.map((obj) => (
            <FixedObjectiveCard key={obj.id} objective={obj} queryClient={queryClient} />
          ))}
        </div>
      )}
    </div>
  );
}

function FixedObjectiveCard({ objective, queryClient }: { objective: FixedObjective; queryClient: any }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(objective.name);
  const [description, setDescription] = useState(objective.description ?? '');
  const [emoji, setEmoji] = useState(objective.emoji ?? '');
  const [config, setConfig] = useState(JSON.stringify(objective.config ?? {}, null, 2));
  const [active, setActive] = useState(objective.active);

  const updateMutation = useMutation({
    mutationFn: (dto: Partial<FixedObjective>) =>
      storeService.updateFixedObjective(objective.id, dto),
    onSuccess: () => {
      toast.success('Objetivo atualizado!');
      queryClient.invalidateQueries({ queryKey: ['fixed-objectives'] });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    let parsedConfig: Record<string, unknown> | undefined;
    try {
      parsedConfig = JSON.parse(config);
    } catch {
      toast.error('JSON inválido — verifique a configuração JSON');
      return;
    }
    updateMutation.mutate({ name, description, emoji, config: parsedConfig, active });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{objective.emoji || '🎯'}</span>
            <div>
              <CardTitle className="text-base">{objective.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs font-mono">{objective.key}</Badge>
                <Badge variant={objective.active ? 'default' : 'secondary'} className="text-xs">
                  {objective.active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            {isEditing ? 'Cancelar' : 'Editar'}
          </Button>
        </div>
        {!isEditing && objective.description && (
          <CardDescription className="mt-2">{objective.description}</CardDescription>
        )}
      </CardHeader>

      {isEditing && (
        <CardContent className="space-y-4 border-t pt-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label>Emoji</Label>
              <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="🔥" maxLength={10} className="text-center text-xl" />
            </div>
            <div className="col-span-3 space-y-2">
              <Label>Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" />
              Configuração (JSON)
            </Label>
            <Textarea
              value={config}
              onChange={(e) => setConfig(e.target.value)}
              rows={6}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Edite os parâmetros do objetivo. Os campos dependem do tipo de objetivo (ex: streakForBonus, bonusXpPerTask).
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label>Objetivo ativo</Label>
          </div>

          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            <Save className="h-4 w-4 mr-1.5" />
            Salvar alterações
          </Button>
        </CardContent>
      )}

      {!isEditing && objective.config && (
        <CardContent className="pt-0">
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors py-1">
              Ver configuração atual
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded-md overflow-x-auto text-[10px] font-mono">
              {JSON.stringify(objective.config, null, 2)}
            </pre>
          </details>
        </CardContent>
      )}
    </Card>
  );
}

// ===========================
// XP RULES TAB
// ===========================

type XpRuleRow = {
  id?: string;
  taskType: string;
  label: string;
  points: number;
  dailyLimit: number;
  active: boolean;
  sortOrder: number;
};

function XpRulesTab() {
  const queryClient = useQueryClient();
  const [editedRules, setEditedRules] = useState<XpRuleRow[] | null>(null);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['admin-xp-rules'],
    queryFn: gamificationService.getXpRules,
  });

  const displayRules = editedRules ?? rules;

  const updateMutation = useMutation({
    mutationFn: gamificationService.updateXpRules,
    onSuccess: () => {
      toast.success('Regras de pontos atualizadas!');
      queryClient.invalidateQueries({ queryKey: ['admin-xp-rules'] });
      queryClient.invalidateQueries({ queryKey: ['store-admin-insights'] });
      setEditedRules(null);
    },
  });

  const handleFieldChange = (
    taskType: string,
    field: 'label' | 'points' | 'dailyLimit' | 'active',
    value: string | boolean,
  ) => {
    const base = editedRules ?? rules;
    setEditedRules(
      base.map((rule) => {
        if (rule.taskType !== taskType) return rule;
        if (field === 'active') return { ...rule, active: value as boolean };
        if (field === 'label') return { ...rule, label: value as string };
        if (field === 'points') return { ...rule, points: parseFloat(value as string) || 0 };
        if (field === 'dailyLimit') return { ...rule, dailyLimit: parseInt(value as string, 10) || 0 };
        return rule;
      }),
    );
  };

  const handleSave = () => {
    if (!editedRules) return;
    updateMutation.mutate(
      editedRules.map((rule) => ({
        taskType: rule.taskType,
        label: rule.label,
        points: rule.points,
        dailyLimit: rule.dailyLimit,
        active: rule.active,
        sortOrder: rule.sortOrder,
      })),
    );
  };

  const hasChanges = editedRules !== null;

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10">
        <CardContent className="pt-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Configure quantos pontos cada tipo de ação concede e o limite diário por usuário.
            Alterações valem apenas para novas conclusões — estornos usam o valor original da transação.
          </p>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-muted-foreground py-12 text-center">Carregando regras...</div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-yellow-500" />
              Regras de Pontos por Tipo
            </CardTitle>
            <CardDescription>
              Pontos por ação e limite diário de cada tipo de tarefa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 pr-4 font-medium">Tipo</th>
                    <th className="pb-3 pr-4 font-medium">Label</th>
                    <th className="pb-3 pr-4 font-medium">Pontos/ação</th>
                    <th className="pb-3 pr-4 font-medium">Limite diário</th>
                    <th className="pb-3 pr-4 font-medium">Máx/dia</th>
                    <th className="pb-3 font-medium">Ativo</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRules.map((rule) => (
                    <tr key={rule.taskType} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className="font-mono text-xs">
                          {rule.taskType}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Input
                          value={rule.label}
                          onChange={(e) => handleFieldChange(rule.taskType, 'label', e.target.value)}
                          className="h-8 min-w-[140px]"
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={rule.points}
                          onChange={(e) => handleFieldChange(rule.taskType, 'points', e.target.value)}
                          className="h-8 w-24"
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={rule.dailyLimit}
                          onChange={(e) => handleFieldChange(rule.taskType, 'dailyLimit', e.target.value)}
                          className="h-8 w-24"
                        />
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground tabular-nums">
                        {(rule.points * rule.dailyLimit).toFixed(1)}
                      </td>
                      <td className="py-3">
                        <Switch
                          checked={rule.active}
                          onCheckedChange={(checked) => handleFieldChange(rule.taskType, 'active', checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-1.5" />
                Salvar alterações
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <TeamXpOverridesCard rules={displayRules} />
    </div>
  );
}

function TeamXpOverridesCard({ rules }: { rules: XpRuleRow[] }) {
  const queryClient = useQueryClient();
  const [editedOverrides, setEditedOverrides] = useState<TeamXpOverrideRow[] | null>(null);
  const [newTeamId, setNewTeamId] = useState('');
  const [newTaskType, setNewTaskType] = useState('');
  const [newPoints, setNewPoints] = useState('1');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-team-xp-overrides'],
    queryFn: gamificationService.getTeamXpOverrides,
  });

  const teams = data?.teams ?? [];
  const displayOverrides = editedOverrides ?? data?.overrides ?? [];

  const saveMutation = useMutation({
    mutationFn: gamificationService.updateTeamXpOverrides,
    onSuccess: () => {
      toast.success('Pontos por equipe atualizados!');
      queryClient.invalidateQueries({ queryKey: ['admin-team-xp-overrides'] });
      setEditedOverrides(null);
    },
  });

  const handleAdd = () => {
    if (!newTeamId || !newTaskType) {
      toast.error('Selecione equipe e tipo de ação');
      return;
    }
    const exists = displayOverrides.some(
      (row) => row.teamId === newTeamId && row.taskType === newTaskType,
    );
    if (exists) {
      toast.error('Já existe um override para essa equipe e tipo');
      return;
    }
    const teamName = teams.find((team) => team.id === newTeamId)?.name;
    setEditedOverrides([
      ...displayOverrides,
      {
        teamId: newTeamId,
        teamName,
        taskType: newTaskType,
        points: parseFloat(newPoints) || 0,
        active: true,
      },
    ]);
    setNewTeamId('');
    setNewTaskType('');
    setNewPoints('1');
  };

  const handleFieldChange = (
    index: number,
    field: 'points' | 'active',
    value: string | boolean,
  ) => {
    const next = displayOverrides.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      if (field === 'active') return { ...row, active: value as boolean };
      return { ...row, points: parseFloat(value as string) || 0 };
    });
    setEditedOverrides(next);
  };

  const handleRemove = (index: number) => {
    setEditedOverrides(displayOverrides.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleSave = () => {
    saveMutation.mutate(
      displayOverrides.map((row) => ({
        teamId: row.teamId,
        taskType: row.taskType,
        points: row.points,
        active: row.active,
      })),
    );
  };

  const ruleLabel = (taskType: string) =>
    rules.find((rule) => rule.taskType === taskType)?.label || taskType;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-500" />
          Pontos por equipe
        </CardTitle>
        <CardDescription>
          Substitui a regra base para membros da equipe. Se o usuário estiver em mais de uma
          equipe com override no mesmo tipo, vale o maior valor. O limite diário continua o da
          regra base.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="text-muted-foreground py-8 text-center">Carregando overrides...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 pr-4 font-medium">Equipe</th>
                    <th className="pb-3 pr-4 font-medium">Tipo de ação</th>
                    <th className="pb-3 pr-4 font-medium">Pontos</th>
                    <th className="pb-3 pr-4 font-medium">Ativo</th>
                    <th className="pb-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {displayOverrides.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-muted-foreground">
                        Nenhuma equipe com pontuação específica.
                      </td>
                    </tr>
                  ) : (
                    displayOverrides.map((row, index) => (
                      <tr key={`${row.teamId}-${row.taskType}`} className="border-b last:border-0">
                        <td className="py-3 pr-4">{row.teamName || row.teamId}</td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className="font-mono text-xs">
                            {ruleLabel(row.taskType)}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={row.points}
                            onChange={(e) => handleFieldChange(index, 'points', e.target.value)}
                            className="h-8 w-24"
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <Switch
                            checked={row.active}
                            onCheckedChange={(checked) =>
                              handleFieldChange(index, 'active', checked)
                            }
                          />
                        </td>
                        <td className="py-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(index)}
                            className="h-8 w-8 p-0 text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label>Equipe</Label>
                <Select value={newTeamId} onValueChange={setNewTeamId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Selecionar equipe" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={newTaskType} onValueChange={setNewTaskType}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Tipo de ação" />
                  </SelectTrigger>
                  <SelectContent>
                    {rules.map((rule) => (
                      <SelectItem key={rule.taskType} value={rule.taskType}>
                        {rule.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Pontos</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={newPoints}
                  onChange={(e) => setNewPoints(e.target.value)}
                  className="h-10 w-24"
                />
              </div>
              <Button type="button" variant="outline" onClick={handleAdd}>
                <Plus className="h-4 w-4 mr-1.5" />
                Adicionar
              </Button>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={editedOverrides === null || saveMutation.isPending}
              >
                <Save className="h-4 w-4 mr-1.5" />
                Salvar overrides
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
